import {
  Component,
  signal,
  computed,
  inject,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LucideAngularModule } from 'lucide-angular';
import { DashboardService } from '../../../features/dashboard/services/dashboard.service';
import { ProjectsDataDto } from '../../models/dashboard.dto';
import { AssignedRequestItemDto, PendingApprovalItemDto } from '../../models/global-search.dto';
import { GlobalSearchService } from '../../services/global-search.service';
import { RecentItemsService, RecentSearchItem } from '../../services/recent-items.service';
import { getRelativeTime } from '../../utils/relative-time.util';
import { ToolbarPopoverComponent } from '../toolbar-popover/toolbar-popover.component';
import { HubspotMarkComponent } from '../hubspot-mark/hubspot-mark.component';
import { SearchFilterPanelComponent } from './components/search-filter-panel/search-filter-panel.component';
import {
  DEFAULT_ASSIGNED_STATUSES,
  DEFAULT_SEARCH_SORT,
  REQUEST_STATUS_OPTIONS,
  SORT_TO_ORDERING,
  SearchSort,
} from './search-filters';

type SearchTab = 'recent' | 'all-projects' | 'requests-assigned' | 'pending-approvals';

type PriorityTone = 'destructive' | 'warning' | 'success';

// Flat item model — optional fields are type-specific
interface FlatItem {
  type: 'section' | 'nav' | 'project' | 'sub-action' | 'recent' | 'active-request' | 'pending-approval';
  id: string;
  label: string;
  icon?: string;
  route?: string;
  queryParams?: Record<string, string>;
  // project / recent items
  color?: string;
  description?: string;
  projectId?: string;
  // section items
  count?: number;
  // request rows
  subtitle?: string;
  priority?: string;
  priorityTone?: PriorityTone;
  // pending-approval rows
  requester?: string;
  avatarUrl?: string | null;
  initials?: string;
  /** Requested from HubSpot: the sender has no avatar, so HubSpot's logo stands in. */
  fromHubspot?: boolean;
  timeAgo?: string;
  // disabled nav entries ("Coming Soon")
  disabled?: boolean;
  disabledTag?: string;
}

interface StaticNavEntry {
  id: string;
  label: string;
  icon: string;
  route: string;
  disabled?: boolean;
  disabledTag?: string;
}

const NAV_ENTRIES: StaticNavEntry[] = [
  { id: 'nav-requests',  label: 'Requests',      icon: 'clipboard-list',   route: '/requests'  },
  { id: 'nav-sites',     label: 'Sites Updates', icon: 'globe',            route: '/sites'     },
  { id: 'nav-workflows', label: 'Workflows',     icon: 'workflow',         route: '',           disabled: true, disabledTag: 'Coming Soon' },
  { id: 'nav-aeo',       label: 'AEO',           icon: 'message-circle',  route: '',           disabled: true, disabledTag: 'Coming Soon' },
];

const PROJECT_SUB_ACTIONS = [
  { key: 'requests', label: 'Requests', icon: 'clipboard-list', route: '/requests' },
  { key: 'sites',    label: 'Sites',    icon: 'layers',         route: '/sites'    },
] as const;

/** Where choosing a project in the palette lands. */
const PROJECT_LANDING_ROUTE = '/sites';

const TAB_DEFS: { id: SearchTab; label: string }[] = [
  { id: 'recent',              label: 'Recent' },
  { id: 'all-projects',        label: 'All Projects' },
  { id: 'requests-assigned',   label: 'Requests Assigned' },
  { id: 'pending-approvals',   label: 'Pending Approvals' },
];

interface TabEmptyState {
  section: string;
  icon: string;
  title: string;
  description: string;
}

// Tabs whose section header stays visible (count 0) above a dedicated empty state
const TAB_EMPTY_STATES: Partial<Record<SearchTab, TabEmptyState>> = {
  'requests-assigned': {
    section: 'Requests Assigned', icon: 'square-check', title: 'No assigned tasks',
    description: 'When a request or approval is assigned to you, it will appear here.',
  },
  'pending-approvals': {
    section: 'Pending Approvals', icon: 'thumbs-up', title: 'No pending approvals',
    description: 'When team members send requests for your approval, they will appear here.',
  },
};

const PRIORITY_TONES: Record<string, PriorityTone> = {
  asap: 'destructive', high: 'destructive', medium: 'warning', low: 'success',
};

const REQUEST_LIST_LIMIT = 50;

const PROJECT_SORTERS: Record<SearchSort, (a: ProjectsDataDto, b: ProjectsDataDto) => number> = {
  name:    (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  updated: (a, b) => b.updated_at.localeCompare(a.updated_at),
  created: (a, b) => b.created_at.localeCompare(a.created_at),
};

@Component({
  selector: 'project-search',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, ToolbarPopoverComponent, SearchFilterPanelComponent, HubspotMarkComponent],
  templateUrl: './project-search.component.html',
  styleUrls: ['./project-search.component.scss']
})
export class ProjectSearchComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput')   private inputRef!:   ElementRef<HTMLInputElement>;
  @ViewChildren('searchItem') private itemRefs!:   QueryList<ElementRef<HTMLButtonElement>>;
  // Lives inside an @if block, so the chip row (outside it) reaches it through here
  @ViewChild('filterMenu')    private filterMenu?: ToolbarPopoverComponent;

  private readonly dashboardService  = inject(DashboardService);
  private readonly globalSearchService = inject(GlobalSearchService);
  private readonly recentItemsService = inject(RecentItemsService);
  private readonly router            = inject(Router);

  readonly tabDefs     = TAB_DEFS;
  readonly isOpen      = signal(false);
  readonly searchQuery = signal('');
  readonly activeIndex = signal(-1);
  readonly isLoading   = signal(false);
  readonly activeTab   = signal<SearchTab>('all-projects');

  private readonly allProjects       = signal<ProjectsDataDto[]>([]);
  private readonly assignedRequests  = signal<AssignedRequestItemDto[]>([]);
  private readonly pendingApprovals  = signal<PendingApprovalItemDto[]>([]);
  private readonly assignedLoading   = signal(false);
  private readonly pendingLoading    = signal(false);
  // Filters — kept while the component lives, so they survive closing and reopening the modal
  readonly projectsSort     = signal<SearchSort>(DEFAULT_SEARCH_SORT);
  readonly assignedSort     = signal<SearchSort>(DEFAULT_SEARCH_SORT);
  readonly pendingSort      = signal<SearchSort>(DEFAULT_SEARCH_SORT);
  readonly assignedStatuses = signal<readonly string[]>(DEFAULT_ASSIGNED_STATUSES);
  readonly requestStatusOptions = REQUEST_STATUS_OPTIONS;

  private refreshSub?: Subscription;
  private assignedSub?: Subscription;
  private pendingSub?: Subscription;

  // ── Per-tab flat lists (all share the same FlatItem shape driving the template + keyboard nav) ──

  private readonly recentFlatItems = computed<FlatItem[]>(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const items = this.recentItemsService.items()
      .filter(r => !q || r.label.toLowerCase().includes(q))
      .map<FlatItem>(r => ({
        type: 'recent', id: `recent-${r.id}`, label: r.label,
        icon: r.icon, color: r.color, description: r.subtitle,
        // Project recents stored before the landing route changed still point at the old one
        route: r.type === 'project' ? PROJECT_LANDING_ROUTE : r.route, queryParams: r.queryParams,
      }));

    if (items.length === 0) return [];
    return [{ type: 'section', id: 'sec-recent', label: 'Recent', count: items.length }, ...items];
  });

  private readonly allProjectsFlatItems = computed<FlatItem[]>(() => {
    const q    = this.searchQuery().toLowerCase().trim();
    const items: FlatItem[] = [];

    // ── Navigation shortcuts ─────────────────────────────────────────────
    const navItems = NAV_ENTRIES
      .filter(e => !q || e.label.toLowerCase().includes(q))
      .map<FlatItem>(e => ({
        type: 'nav', id: e.id, label: e.label, icon: e.icon, route: e.route,
        disabled: e.disabled, disabledTag: e.disabledTag,
      }));

    if (navItems.length > 0) {
      items.push({ type: 'section', id: 'sec-nav', label: 'Jump to' });
      items.push(...navItems);
    }

    // ── Projects (+ sub-actions when querying) ───────────────────────────
    const matched = (q
      ? this.allProjects().filter(p =>
          p.name.toLowerCase().includes(q) ||
          (p.description?.toLowerCase().includes(q) ?? false))
      : [...this.allProjects()]
    ).sort(PROJECT_SORTERS[this.projectsSort()]);

    if (matched.length > 0) {
      items.push({
        type: 'section', id: 'sec-projects',
        label: q ? 'Projects' : 'All Projects',
        count: matched.length,
      });

      for (const project of matched) {
        items.push({
          type: 'project', id: `proj-${project.id}`,
          label: project.name, description: project.description,
          color: project.color, projectId: project.id,
          route: PROJECT_LANDING_ROUTE, queryParams: { projectId: project.id },
        });

        // Sub-actions only when user is actively searching
        if (q) {
          for (const sub of PROJECT_SUB_ACTIONS) {
            items.push({
              type: 'sub-action', id: `sub-${project.id}-${sub.key}`,
              label: sub.label, icon: sub.icon,
              route: sub.route, queryParams: { projectId: project.id },
              color: project.color,
            });
          }
        }
      }
    }

    return items;
  });

  private readonly requestsAssignedFlatItems = computed<FlatItem[]>(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const matched = this.assignedRequests().filter(r =>
      !q ||
      r.name.toLowerCase().includes(q) ||
      r.project.name.toLowerCase().includes(q));

    if (matched.length === 0) return [];

    const items: FlatItem[] = [
      { type: 'section', id: 'sec-requests-assigned', label: 'Requests Assigned', count: matched.length },
    ];
    for (const r of matched) {
      items.push({
        type: 'active-request', id: `req-${r.id}`, label: r.name,
        subtitle: r.location ? `${r.project.name} / ${r.location}` : r.project.name,
        priority: r.priority?.name,
        priorityTone: r.priority ? PRIORITY_TONES[r.priority.name.toLowerCase()] : undefined,
        route: '/requests', queryParams: { projectId: r.project.id, requestId: r.id },
      });
    }
    return items;
  });

  private readonly pendingApprovalsFlatItems = computed<FlatItem[]>(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const matched = this.pendingApprovals().filter(r => {
      const requester = requesterName(r);
      return !q ||
        r.name.toLowerCase().includes(q) ||
        r.project.name.toLowerCase().includes(q) ||
        requester.toLowerCase().includes(q);
    });

    if (matched.length === 0) return [];

    const items: FlatItem[] = [
      { type: 'section', id: 'sec-pending-approvals', label: 'Pending Approvals', count: matched.length },
    ];
    for (const r of matched) {
      const { first_name, last_name, avatar } = r.requested_by;
      const fromHubspot = !!r.hubspot_requester;
      items.push({
        type: 'pending-approval', id: `awaiting-${r.id}`, label: r.name,
        subtitle: r.project.name,
        requester: requesterName(r),
        avatarUrl: fromHubspot ? null : avatar,
        initials: fromHubspot ? '' : `${first_name.charAt(0)}${last_name.charAt(0)}`,
        fromHubspot,
        timeAgo: getRelativeTime(r.sent_for_approval_at),
        route: '/requests', queryParams: { projectId: r.project.id, requestId: r.id },
      });
    }
    return items;
  });

  readonly filteredFlatItems = computed<FlatItem[]>(() => {
    switch (this.activeTab()) {
      case 'recent':             return this.recentFlatItems();
      case 'requests-assigned':  return this.requestsAssignedFlatItems();
      case 'pending-approvals':  return this.pendingApprovalsFlatItems();
      case 'all-projects':
      default:                   return this.allProjectsFlatItems();
    }
  });

  readonly hasResults = computed(() =>
    this.filteredFlatItems().some(i => i.type !== 'section')
  );

  readonly tabEmptyState = computed(() => TAB_EMPTY_STATES[this.activeTab()]);

  /** Tints the Filter button: the active tab's filter is away from its default. */
  readonly filterActive = computed(() => {
    switch (this.activeTab()) {
      case 'all-projects':      return this.projectsSort() !== DEFAULT_SEARCH_SORT;
      case 'requests-assigned': return this.assignedSort() !== DEFAULT_SEARCH_SORT
        || !sameMembers(this.assignedStatuses(), DEFAULT_ASSIGNED_STATUSES);
      case 'pending-approvals': return this.pendingSort() !== DEFAULT_SEARCH_SORT;
      default:                  return false;
    }
  });

  readonly statusChips = computed(() =>
    this.activeTab() === 'requests-assigned'
      ? REQUEST_STATUS_OPTIONS.filter(o => this.assignedStatuses().includes(o.value))
      : []
  );

  // A request list only spins on its first load; refreshes keep showing the rows already there
  readonly showLoading = computed(() => {
    if (this.isLoading()) return true;
    switch (this.activeTab()) {
      case 'requests-assigned': return this.assignedLoading() && this.assignedRequests().length === 0;
      case 'pending-approvals': return this.pendingLoading() && this.pendingApprovals().length === 0;
      default:                  return false;
    }
  });

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadDashboard();
    this.refreshSub = this.dashboardService.refreshDashboard$.subscribe(() => {
      this.loadDashboard(true);
    });
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
    this.assignedSub?.unsubscribe();
    this.pendingSub?.unsubscribe();
  }

  private loadDashboard(forceRefresh = false): void {
    const cached = this.dashboardService.getCachedDashboardData();
    if (cached && !forceRefresh) {
      this.applyDashboardData(cached);
      return;
    }
    this.isLoading.set(true);
    this.dashboardService.getDashboardData().subscribe({
      next:  res => { this.applyDashboardData(res.data); this.isLoading.set(false); },
      error: ()  => this.isLoading.set(false),
    });
  }

  private applyDashboardData(data: { projects?: ProjectsDataDto[] }): void {
    this.allProjects.set(data.projects ?? []);
  }

  /**
   * Both lists are per-user and change often, so they refresh on every open and on every filter
   * change. Sorting and status filtering happen server-side: the lists are capped, so doing it
   * here would only reorder a truncated set. A newer load cancels the one in flight so a stale
   * response can't overwrite it.
   */
  private loadRequestLists(): void {
    this.loadAssigned();
    this.loadPending();
  }

  private loadAssigned(): void {
    this.assignedSub?.unsubscribe();
    this.assignedLoading.set(true);
    this.assignedSub = this.globalSearchService
      .getAssignedToMe({
        ordering: SORT_TO_ORDERING[this.assignedSort()],
        status: this.assignedStatuses(),
        limit: REQUEST_LIST_LIMIT,
      })
      .subscribe({
        next:  res => { this.assignedRequests.set(res.data ?? []); this.assignedLoading.set(false); },
        error: ()  => this.assignedLoading.set(false),
      });
  }

  private loadPending(): void {
    this.pendingSub?.unsubscribe();
    this.pendingLoading.set(true);
    this.pendingSub = this.globalSearchService
      .getPendingMyApproval({ ordering: SORT_TO_ORDERING[this.pendingSort()], limit: REQUEST_LIST_LIMIT })
      .subscribe({
        next:  res => { this.pendingApprovals.set(res.data ?? []); this.pendingLoading.set(false); },
        error: ()  => this.pendingLoading.set(false),
      });
  }

  // ── Filters ───────────────────────────────────────────────────────────────

  setAssignedSort(sort: SearchSort): void {
    this.assignedSort.set(sort);
    this.loadAssigned();
  }

  /** None ticked means no status filter: every assigned request. */
  setAssignedStatuses(statuses: readonly string[]): void {
    this.assignedStatuses.set(statuses);
    this.loadAssigned();
  }

  removeAssignedStatus(value: string): void {
    this.setAssignedStatuses(this.assignedStatuses().filter(s => s !== value));
  }

  openFilterMenu(): void {
    this.filterMenu?.open();
  }

  setPendingSort(sort: SearchSort): void {
    this.pendingSort.set(sort);
    this.loadPending();
  }

  // ── Panel open / close ────────────────────────────────────────────────────

  open(): void {
    if (this.isOpen()) return;
    this.isOpen.set(true);
    this.activeIndex.set(-1);
    this.activeTab.set(this.recentItemsService.items().length > 0 ? 'recent' : 'all-projects');
    this.loadRequestLists();
    setTimeout(() => this.inputRef?.nativeElement.focus(), 0);
  }

  close(): void {
    this.isOpen.set(false);
    this.searchQuery.set('');
    this.activeIndex.set(-1);
    this.inputRef?.nativeElement.blur();
  }

  selectTab(tab: SearchTab): void {
    this.activeTab.set(tab);
    this.activeIndex.set(-1);
  }

  // ── Input events ──────────────────────────────────────────────────────────

  onInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.activeIndex.set(-1);
  }

  onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter':
        event.preventDefault();
        const idx = this.activeIndex();
        if (idx >= 0) this.selectItem(this.filteredFlatItems()[idx]);
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  // ── Keyboard navigation helpers ───────────────────────────────────────────

  private moveActive(dir: 1 | -1): void {
    const next = this.adjacentSelectableIndex(this.activeIndex(), dir);
    this.activeIndex.set(next);
    this.scrollActiveIntoView(next);
  }

  private adjacentSelectableIndex(from: number, dir: 1 | -1): number {
    const items = this.filteredFlatItems();
    let idx = from === -1 && dir === 1 ? 0 : from + dir;
    while (idx >= 0 && idx < items.length) {
      if (items[idx].type !== 'section' && !items[idx].disabled) return idx;
      idx += dir;
    }
    return from;
  }

  private scrollActiveIntoView(flatIdx: number): void {
    if (flatIdx < 0) return;
    const items = this.filteredFlatItems();
    // Map flat index → button index (sections + disabled entries have no DOM button)
    let btnIdx = 0;
    for (let i = 0; i < flatIdx; i++) {
      if (items[i].type !== 'section' && !items[i].disabled) btnIdx++;
    }
    setTimeout(() => {
      this.itemRefs?.toArray()[btnIdx]?.nativeElement
        .scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }

  // ── Selection ─────────────────────────────────────────────────────────────

  selectItem(item: FlatItem): void {
    if (item.type === 'section' || item.disabled) return;

    // Every selectable item carries route + queryParams
    this.router.navigate([item.route!], { queryParams: item.queryParams });

    this.recordRecent(item);
    this.close();
  }

  private recordRecent(item: FlatItem): void {
    if (item.type === 'recent' || !item.route) return;

    const recentItem: Omit<RecentSearchItem, 'visitedAt'> = {
      id: item.id,
      type: item.type as RecentSearchItem['type'],
      label: item.label,
      subtitle: item.description ?? item.subtitle,
      icon: item.icon,
      color: item.color,
      route: item.route,
      queryParams: item.queryParams,
    };
    this.recentItemsService.record(recentItem);
  }

  // ── Global shortcut + Escape ──────────────────────────────────────────────

  @HostListener('document:keydown', ['$event'])
  onGlobalKeyDown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.isOpen() ? this.close() : this.open();
      return;
    }
    // The filter popover handles (and prevents) its own Esc; that one closes only the popover
    if (event.key === 'Escape' && this.isOpen() && !event.defaultPrevented) {
      this.close();
    }
  }
}

function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every(v => b.includes(v));
}

/**
 * Who a pending approval was requested by. A Request HubSpot made names its HubSpot sender, never
 * the requester it is filed under, who is not shown.
 */
function requesterName(item: PendingApprovalItemDto): string {
  if (item.hubspot_requester) return `${item.hubspot_requester.name} via HubSpot`.trim();
  return `${item.requested_by.first_name} ${item.requested_by.last_name}`.trim();
}
